import { useSyncExternalStore } from "react";
import { trackingOptInRequired } from "./consent-regions";

/**
 * Product-analytics consent, one record per account. Every analytics tracker
 * runs only while `allowed` is true; essential processing (sign-in, settings,
 * Sentry error reporting) does not ask.
 *
 * Bridge: records live in this browser until the account record on the
 * server replaces them, so a choice does not yet follow the user to another
 * browser. Storing the choice is strictly necessary to honour it.
 */
export type TrackingChoice = "granted" | "denied";

export type TrackingConsent = {
  /** The saved choice; otherwise off where opt-in is required and on elsewhere. */
  allowed: boolean;
  /** Absent until the user answers the prompt or uses the toggle. */
  choice?: TrackingChoice;
  /** Whether the current location requires opt-in, so the default is off. */
  optInRequired: boolean;
  /** Whether the sign-in prompt is due or open; it stays true until answered or closed. */
  promptDue: boolean;
};

/** A dismissed prompt asks again in later browser sessions, up to this many times in all. */
const TRACKING_PROMPT_LIMIT = 3;
/**
 * Version of the consent copy, stored with each choice. Bump it when the copy
 * changes what someone agrees to: a grant made against another version counts
 * as no choice, and the prompt asks again with a fresh budget; a refusal stands.
 */
const TRACKING_CONSENT_COPY_VERSION = "2026-10-16";

type ConsentRecord = {
  choice?: TrackingChoice;
  decidedAt?: string;
  copyVersion?: string;
  /** Prompts shown for `promptedCopyVersion`; another version starts from none. */
  promptsShown?: number;
  promptedCopyVersion?: string;
  /** The browser session the prompt last opened in. */
  promptedSession?: string;
};

const STORAGE_KEY = "Shuttlerock:TrackingConsent";
const SESSION_COOKIE = "Shuttlerock-TrackingConsentSession";

const listeners = new Set<() => void>();
// Records whose save failed win over storage, so a choice and a shown prompt still apply
// for this page when storage is unavailable or full.
const unsavedRecords = new Map<string, ConsentRecord>();
const pageSession = Math.random().toString(36).slice(2);
let sessionId: string | undefined;
const openPrompts = new Set<string>();
const snapshots = new Map<string, { key: string; consent: TrackingConsent }>();

/** Undefined when storage cannot be read. */
function readRecords(): Record<string, ConsentRecord> | undefined {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : undefined;
    return parsed && typeof parsed === "object" ? (parsed as Record<string, ConsentRecord>) : {};
  } catch {
    return undefined;
  }
}

function writeRecord(principalKey: string, record: ConsentRecord) {
  try {
    const records = { ...readRecords(), [principalKey]: record };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
    unsavedRecords.delete(principalKey);
  } catch {
    unsavedRecords.set(principalKey, record);
  }
}

function recordFor(principalKey: string): ConsentRecord {
  return unsavedRecords.get(principalKey) ?? readRecords()?.[principalKey] ?? {};
}

/**
 * This browser session's id: shared by every tab and gone when the browser
 * closes, so it lives in a cookie with no expiry (sessionStorage is per tab).
 * Without cookies each page is its own session. Read once per page.
 */
function browserSession(): string {
  sessionId ??= readOrStartBrowserSession();
  return sessionId;
}

function readOrStartBrowserSession(): string {
  try {
    const stored = document.cookie
      .split("; ")
      .find((cookie) => cookie.startsWith(`${SESSION_COOKIE}=`))
      ?.slice(SESSION_COOKIE.length + 1);
    if (stored) return stored;
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${SESSION_COOKIE}=${pageSession}; path=/; SameSite=Lax${secure}`;
  } catch {
    // pageSession stands in for the browser session.
  }
  return pageSession;
}

// Built once: constructing the formatter is the costly part, and it is read on every render.
let dateTimeFormat: Intl.DateTimeFormat | undefined;

function browserTimeZone(): string | undefined {
  try {
    dateTimeFormat ??= new Intl.DateTimeFormat();
    return dateTimeFormat.resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

function promptsShownFor(record: ConsentRecord): number {
  return record.promptedCopyVersion === TRACKING_CONSENT_COPY_VERSION
    ? (record.promptsShown ?? 0)
    : 0;
}

function currentChoice(record: ConsentRecord): TrackingChoice | undefined {
  if (record.choice === "denied") return "denied";
  return record.choice === "granted" && record.copyVersion === TRACKING_CONSENT_COPY_VERSION
    ? "granted"
    : undefined;
}

/** Consent for a signed-in account, or for a visitor with no account (no prompt, no choice). */
export function getTrackingConsent(principalKey?: string): TrackingConsent {
  const optInRequired = trackingOptInRequired(browserTimeZone());
  const record = principalKey ? recordFor(principalKey) : {};
  const choice = currentChoice(record);
  const promptDue = Boolean(
    principalKey &&
    optInRequired &&
    !choice &&
    (openPrompts.has(principalKey) ||
      (promptsShownFor(record) < TRACKING_PROMPT_LIMIT &&
        record.promptedSession !== browserSession())),
  );
  const consent: TrackingConsent = {
    allowed: choice ? choice === "granted" : !optInRequired,
    ...(choice ? { choice } : {}),
    optInRequired,
    promptDue,
  };
  // useSyncExternalStore needs the same object while nothing has changed.
  const cacheKey = principalKey ?? "";
  const key = JSON.stringify(consent);
  const cached = snapshots.get(cacheKey);
  if (cached?.key === key) return cached.consent;
  snapshots.set(cacheKey, { key, consent });
  return consent;
}

/** Saves the account's choice from the prompt or the Preferences toggle. */
export function setTrackingConsent(principalKey: string, allowed: boolean) {
  writeRecord(principalKey, {
    ...recordFor(principalKey),
    choice: allowed ? "granted" : "denied",
    decidedAt: new Date().toISOString(),
    copyVersion: TRACKING_CONSENT_COPY_VERSION,
  });
  openPrompts.delete(principalKey);
  notify();
}

/**
 * Counts the prompt as shown and keeps it from opening again this browser
 * session. Idempotent while it is open, so a re-render does not count twice,
 * and a no-op when another tab of the session has already opened it.
 */
export function openTrackingPrompt(principalKey: string) {
  if (openPrompts.has(principalKey)) return;
  const record = recordFor(principalKey);
  if (record.promptedSession !== browserSession()) {
    openPrompts.add(principalKey);
    writeRecord(principalKey, {
      ...record,
      promptsShown: promptsShownFor(record) + 1,
      promptedCopyVersion: TRACKING_CONSENT_COPY_VERSION,
      promptedSession: browserSession(),
    });
  }
  notify();
}

/** Closing without answering is no consent: analytics stays off and a later browser session asks again. */
export function closeTrackingPrompt(principalKey: string) {
  if (!openPrompts.delete(principalKey)) return;
  notify();
}

function notify() {
  for (const listener of listeners) listener();
}

function onStorage(event: StorageEvent) {
  if (event.key === STORAGE_KEY || event.key === null) notify();
}

/** Calls `listener` whenever consent may have changed, including from another tab. */
export function subscribeTrackingConsent(listener: () => void) {
  listeners.add(listener);
  if (listeners.size === 1) window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) window.removeEventListener("storage", onStorage);
  };
}

export function useTrackingConsent(principalKey?: string): TrackingConsent {
  return useSyncExternalStore(subscribeTrackingConsent, () => getTrackingConsent(principalKey));
}
